'use client';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useReducedMotion } from 'motion/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
/**
 * "On this page" list: a heading, then the sections on a 1px track with a brand bar that slides to the active one.
 * Presentational only — the caller owns the scrollspy (`activeId`) and the jump (`onSelect`), and wraps it in its
 * own sticky <nav>. Used by the job form and the job description read view so both outlines look the same.
 */
export function OutlineNav({
  items,
  activeId,
  onSelect,
  title = 'On this page',
  titleId
}) {
  const reduce = useReducedMotion();
  const listRef = useRef(null);
  const rowRefs = useRef(new Map());
  const [bar, setBar] = useState(null);
  // The bar only animates after its first placement, so it doesn't slide in from the top on mount.
  const [barReady, setBarReady] = useState(false);
  const measure = () => {
    const row = activeId ? rowRefs.current.get(activeId) : undefined;
    if (!row) return;
    const next = {
      top: row.offsetTop,
      height: row.offsetHeight
    };
    setBar(prev => prev && prev.top === next.top && prev.height === next.height ? prev : next);
  };

  // Re-measured every render: rows change height as their description or wrapping changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(measure);

  // Labels re-wrap when the column resizes.
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(list);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);
  useEffect(() => {
    if (!bar || barReady) return;
    const frame = requestAnimationFrame(() => setBarReady(true));
    return () => cancelAnimationFrame(frame);
  }, [bar, barReady]);
  return <>
            <Typography id={titleId} variant="caption" color="text.secondary" fontWeight={600} className="block px-3 mt-2! mb-3! uppercase tracking-wide">
                {title}
            </Typography>
            <ol ref={listRef} className="relative flex flex-col gap-1 border-l border-border ml-px">
                {bar && <span aria-hidden className="absolute -left-px top-0 w-0.5 rounded-full bg-brand" style={{
        height: bar.height,
        transform: `translateY(${bar.top}px)`,
        transition: barReady && !reduce ? `transform 200ms ${EASE_OUT}, height 200ms ${EASE_OUT}` : undefined
      }} />}
                {items.map(item => {
        const isActive = activeId === item.id;
        return <li key={item.id} ref={el => {
          if (el) rowRefs.current.set(item.id, el);else rowRefs.current.delete(item.id);
        }}>
                            <button type="button" onClick={() => onSelect(item.id)} aria-current={isActive ? 'location' : undefined} className={clsx('w-full flex items-center gap-2 rounded-r-md pr-2 py-1 text-left cursor-pointer transition-colors duration-150 ease-out hover:bg-hover focus-visible:outline-2 focus-visible:outline-brand focus-visible:-outline-offset-2', item.indent ? 'pl-6' : 'pl-3')} data-testid={item['data-testid']}>
                                <span className="min-w-0 flex-1">
                                    {/* Sub-sections read a step smaller than their parent (body2 is 12px here), same line height. */}
                                    <Typography variant="body2" component="span" className={clsx('block', item.indent && 'text-[11px]!')} color={isActive ? 'text.primary' : 'text.secondary'} fontWeight={isActive ? 600 : 400}>
                                        {item.label}
                                    </Typography>
                                    {item.description != null && <Typography variant="caption" color={item.descriptionColor ?? 'text.secondary'} noWrap className="flex! items-center gap-1">
                                            {item.description}
                                        </Typography>}
                                </span>
                                {item.trailing}
                            </button>
                        </li>;
      })}
            </ol>
        </>;
}